import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  Dialog,
  DialogTrigger,
  Content,
  Flex,
  View,
  Text,
  Button,
  Heading,
  Form
} from '@adobe/react-spectrum';
import styles from './EditEventModal.module.scss';
import closeIcon from '../../../assets/icons/close-20.svg';
import { events as eventFormConfig } from './store/formFields/events';
import { DropdownComponent } from '../RsdForms/CreateMoment/components/DropdownComponent';
import { TextFieldComponent } from '../RsdForms/CreateMoment/components/TextFieldComponent';
import DateRangePickerComponent from '../RsdForms/CreateMoment/components/DateRangePickerComponent';
import { fetchEditEventTemplate, updateMoment } from './api';
import { EnhancedAlertBanner } from './EnhancedAlertBanner';
import { STATUS_VALUES } from 'utils/constants';

import {
  clearEventsCache,
  clearParentMomentsCache,
  deleteMoment
} from '../../services/momentsApi';
import { ToastQueue } from '@adobe/react-spectrum';
import { useDispatch } from 'react-redux';
import { useHomeSlice } from '../../pages/Home/slice';

interface EditEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialValues?: Record<string, any>;
  itemType?: string;
  issueId?: string;
  onSuccess?: () => void;
  isOverdue?: boolean; // ✅ NEW: Flag to indicate if dates are overdue (past current date/month/quarter)
}

export const EditEventModal: React.FC<EditEventModalProps> = ({
  isOpen,
  onClose,
  initialValues = {},
  itemType = 'Event',
  issueId,
  onSuccess,
  isOverdue = false // ✅ NEW: Default to false if not provided
}) => {
  const dispatch = useDispatch();
  const { actions } = useHomeSlice();

  const [formValues, setFormValues] =
    useState<Record<string, any>>(initialValues);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [template, setTemplate] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cacheCleared, setCacheCleared] = useState(false); // Track if cache was already cleared
  const [alert, setAlert] = useState<{
    message: string;
    variant: 'success' | 'error' | 'info';
  } | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  // Helper to get initial value for a field
  const getInitialValue = (field: any) => {
    // Priority: initialValues > template.dummyValues > field.defaultValue > ''
    if (initialValues[field.name] !== undefined)
      return initialValues[field.name];
    if (template?.dummyValues && template.dummyValues[field.name] !== undefined)
      return template.dummyValues[field.name];
    if (field.defaultValue !== undefined) return field.defaultValue;
    return '';
  };

  useEffect(() => {
    // Populate form values from initialValues and template defaults
    if (template && isOpen) {
      try {
        const newValues: Record<string, any> = {};
        template.sections.forEach((section: any) => {
          if (section && section.fields && Array.isArray(section.fields)) {
            section.fields.forEach((field: any) => {
              if (field && field.name) {
                // Special logic for mapping fields from initialValues
                if (field.name === 'momentType' && initialValues.projectType) {
                  newValues[field.name] = initialValues.projectType;
                } else if (field.name === 'name' && initialValues.eventName) {
                  newValues[field.name] = initialValues.eventName;
                } else if (field.name === 'name' && initialValues.title) {
                  newValues[field.name] = initialValues.title;
                } else if (field.name === 'scheduleRange') {
                  // Handle multiple date formats from the API mapping

                  // First, check if we already have the mapped scheduleRange value (month-based or date-based)
                  if (initialValues.scheduleRange) {
                    newValues[field.name] = initialValues.scheduleRange;
                  } else {
                    // Fallback: handle direct date fields and nested date objects
                    const startDate =
                      initialValues.startDate ||
                      initialValues.dates?.targetStart;
                    const endDate =
                      initialValues.endDate || initialValues.dates?.targetEnd;
                    const targetMonth = initialValues.dates?.targetMonth;
                    const targetEndMonth = initialValues.dates?.targetEndMonth;

                    if (targetMonth) {
                      // Handle month-based dates in fallback
                      const months = targetEndMonth
                        ? [targetMonth, targetEndMonth]
                        : [targetMonth];
                      newValues[field.name] = {
                        type: 'months',
                        months: months
                      };
                    } else if (startDate || endDate) {
                      newValues[field.name] = {
                        start: startDate,
                        end: endDate || startDate
                      };
                    } else {
                      // Set empty date range to allow user input
                      newValues[field.name] = { start: '', end: '' };
                    }
                  }
                } else if (field.name === 'dateRange') {
                  // Handle multiple date formats from the API mapping

                  // First, check if we already have the mapped dateRange value (month-based or date-based)
                  if (initialValues.dateRange) {
                    newValues[field.name] = initialValues.dateRange;
                  } else {
                    // Fallback: handle direct date fields and month data
                    const startDate =
                      initialValues.startDate ||
                      initialValues.dates?.targetStart;
                    const endDate =
                      initialValues.endDate || initialValues.dates?.targetEnd;
                    const targetMonth = initialValues.dates?.targetMonth;
                    const targetEndMonth = initialValues.dates?.targetEndMonth;

                    if (targetMonth) {
                      // Handle month-based dates in fallback
                      const months = targetEndMonth
                        ? [targetMonth, targetEndMonth]
                        : [targetMonth];
                      newValues[field.name] = {
                        type: 'months',
                        months: months
                      };
                    } else if (startDate || endDate) {
                      // Fallback: handle direct date fields
                      newValues[field.name] = {
                        start: startDate,
                        end: endDate || startDate
                      };
                    } else {
                      // Set empty date range to allow user input
                      newValues[field.name] = { start: '', end: '' };
                    }
                  }
                } else {
                  newValues[field.name] = getInitialValue(field);
                }
              }
            });
          }
        });
        setFormValues(newValues);
      } catch (error) {
        setFormValues({});
      }
    }
  }, [template, initialValues, isOpen]);

  useEffect(() => {
    setLoading(true);

    fetchEditEventTemplate(itemType)
      .then(data => {
        const templateKey =
          itemType === 'Key Date' || itemType === 'KeyDate'
            ? 'Key Date'
            : 'Event';

        if (data && data[templateKey]) {
          setTemplate(data[templateKey]);
        } else {
          setTemplate(null);
        }
        setLoading(false);
      })
      .catch(error => {
        setTemplate(null);
        setLoading(false);

        setAlert({
          message: 'Failed to load form template. Please try again.',
          variant: 'error'
        });
      });
  }, [itemType]);

  const handleInputChange = (name: string, value: any) => {
    setFormValues(prev => ({ ...prev, [name]: value }));
    if (hasSubmitted) validateForm({ ...formValues, [name]: value });
  };

  const validateForm = (values = formValues) => {
    if (!template) {
      return false;
    }

    const requiredFields = template.sections.flatMap(s =>
      s.fields.filter(f => f.required)
    );
    const errors: Record<string, string> = {};
    let isValid = true;

    requiredFields.forEach(field => {
      if (values[field.name] === undefined || values[field.name] === '') {
        errors[field.name] = `${field.label} is required.`;
        isValid = false;
      }
    });

    setValidationErrors(errors);
    return isValid;
  };

  // Handler for marking as complete
  const handleMarkAsComplete = async () => {
    if (!issueId) {
      setAlert({
        message: 'Cannot mark as complete: Missing item ID.',
        variant: 'error'
      });
      return;
    }

    setIsSubmitting(true);
    setAlert(null);

    try {
      // Update status to completed
      const updatePayload = {
        status: STATUS_VALUES.COMPLETE
      };

      await updateMoment(issueId, updatePayload);

      // Show success notification
      setAlert({
        message: `${itemType} marked as complete successfully!`,
        variant: 'success'
      });

      // Call onSuccess callback to refresh parent data
      if (onSuccess) {
        onSuccess();
      }

      // Close modal after a brief delay to show success message
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error: any) {
      console.error('❌ Error marking as complete from edit modal:', error);

      // Show error notification
      setAlert({
        message:
          error?.message ||
          `Failed to mark ${itemType.toLowerCase()} as complete. Please try again.`,
        variant: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handler for deleting Event/Key Date
  const handleDelete = () => {
    setShowDeleteConfirmation(true);
  };

  // Handler for confirming deletion
  const handleConfirmDelete = async () => {
    if (!issueId) {
      ToastQueue.negative('Unable to delete: Missing item ID', {
        timeout: 5000
      });
      setShowDeleteConfirmation(false);
      return;
    }

    setIsSubmitting(true);
    setShowDeleteConfirmation(false);

    try {
      // Moment delete audit: backend CommonServiceUtil.saveDeleteAuditHistory (always writes).
      // Do not POST /audits here — would duplicate RSD_DELETE in audit_history.
      const result = await deleteMoment(issueId);

      if (result.success && result.status === 204) {
        // Show success toast
        ToastQueue.positive(`${itemType} has been deleted`, { timeout: 5000 });

        // Clear events/parent moments cache to refresh the lists
        clearEventsCache();
        clearParentMomentsCache();

        // Call onSuccess callback to refresh parent data
        if (onSuccess) {
          onSuccess();
        }

        // Close modal
        onClose();
      } else {
        // Show error toast
        ToastQueue.negative(`Unable to delete ${itemType}`, { timeout: 5000 });
        console.error('Delete failed:', result.error || result.message);
      }
    } catch (error) {
      // Show error toast
      ToastQueue.negative(`Unable to delete ${itemType}`, { timeout: 5000 });
      console.error(`Error deleting ${itemType}:`, error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handler for canceling deletion
  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
  };

  const handleSubmit = async () => {
    setHasSubmitted(true);
    setAlert(null); // Clear any previous alerts

    if (!validateForm()) {
      return;
    }

    if (!issueId) {
      setAlert({
        message: 'Issue ID is required to update the moment',
        variant: 'error'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare the update payload to match the moments API format
      const updatePayload: Record<string, any> = {};

      // Transform form values to match API response format
      Object.keys(formValues).forEach(key => {
        const value = formValues[key];
        if (value !== undefined && value !== null && value !== '') {
          switch (key) {
            case 'startDate':
            case 'endDate':
              // Don't add these directly - they'll be handled in the dates object
              break;
            case 'scheduleRange':
            case 'dateRange':
              // Handle date range objects
              if (value && typeof value === 'object') {
                if (!updatePayload.dates) updatePayload.dates = {};
                // Support both calendar date ranges and month ranges
                if (value.type === 'months' && Array.isArray(value.months)) {
                  if (value.months[0])
                    updatePayload.dates.targetMonth = value.months[0];
                  if (value.months[1])
                    updatePayload.dates.targetEndMonth = value.months[1];
                } else {
                  if (value.start)
                    updatePayload.dates.targetStart = value.start;
                  if (value.end) updatePayload.dates.targetEnd = value.end;
                }
              }
              break;
            case 'eventName':
            case 'title':
              // Map to 'name' field
              updatePayload.name = value;
              break;
            case 'projectType':
              // Map to 'momentType' field
              updatePayload.momentType = value;
              break;
            case 'id':
              // Skip - issueId is handled separately
              break;
            case 'description':
              // Map to reportingSummary if needed, or keep as description
              updatePayload.reportingSummary = value;
              break;
            default:
              // Pass through other fields (categories, products, labels, etc.)
              updatePayload[key] = value;
          }
        }
      });

      // Handle individual startDate/endDate if not already in dates object
      if (formValues.startDate || formValues.endDate) {
        if (!updatePayload.dates) updatePayload.dates = {};
        if (formValues.startDate)
          updatePayload.dates.targetStart = formValues.startDate;
        if (formValues.endDate)
          updatePayload.dates.targetEnd = formValues.endDate;
      }

      // Call the API to update the moment
      await updateMoment(issueId, updatePayload);

      console.log(
        '✅ Moment update API call successful, now clearing caches...'
      );

      // ✅ NEW: Dispatch success alert to trigger card highlighting
      dispatch(
        actions.setUpdateSuccessAlert({
          message: 'Card updated successfully',
          issueId: issueId,
          releasePhaseId: initialValues.releasePhaseId || ''
        })
      );

      // Clear both Events and Marketing Moments caches after successful update
      // IMPORTANT: This must happen BEFORE onSuccess() callback to ensure fresh data
      // Guard against multiple calls within the same session
      if (!cacheCleared) {
        try {
          clearEventsCache();
          clearParentMomentsCache();
          setCacheCleared(true); // Mark as cleared to prevent duplicate clearing
        } catch (cacheError) {
          console.error(
            '❌ [EditEventModal] Cache clearing failed (non-blocking):',
            cacheError
          );
          // Continue with success flow even if cache clearing fails
        }
      }

      // Call onSuccess callback to refresh data seamlessly
      if (onSuccess) {
        onSuccess();
      }

      // Success - show success message
      setAlert({
        message: 'Moment updated successfully! Changes will appear shortly.',
        variant: 'success'
      });

      // Close after a brief delay to show success message
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error: any) {
      setAlert({
        message: error?.message || 'Failed to update moment. Please try again.',
        variant: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render a single field
  const renderField = (field: any): React.ReactElement | null => {
    if (!field || typeof field !== 'object' || !field.name) {
      return null;
    }

    const value = formValues[field.name] || '';
    let fieldComponent: React.ReactElement = <span />;
    // Disable momentType field (type of release/moment) - user should not be able to change it
    const isReadOnly =
      field.readOnly || field.disabled || field.name === 'momentType';
    const fieldType = field.type || 'text'; // Default to text if no type

    if (fieldType === 'select') {
      fieldComponent = (
        <DropdownComponent
          field={{ ...field, disabled: isReadOnly }}
          value={value}
          onChange={val => handleInputChange(field.name, val)}
          formValues={formValues}
          validationState={validationErrors[field.name] ? 'invalid' : 'valid'}
        />
      );
    } else if (fieldType === 'text') {
      fieldComponent = (
        <TextFieldComponent
          field={{ ...field, isReadOnly }}
          value={value}
          onChange={(_, val) => handleInputChange(field.name, val)}
          validationState={validationErrors[field.name] ? 'invalid' : 'valid'}
        />
      );
    } else if (fieldType === 'daterangepicker') {
      fieldComponent = (
        <DateRangePickerComponent
          field={field}
          value={value}
          onChange={val => handleInputChange(field.name, val)}
          validationState={validationErrors[field.name] ? 'invalid' : 'valid'}
          isOverdue={isOverdue} // ✅ Pass isOverdue to highlight dates in red
        />
      );
    } else if (fieldType === 'api_dropdown') {
      fieldComponent = (
        <DropdownComponent
          field={{ ...field, disabled: isReadOnly }}
          value={value}
          onChange={val => handleInputChange(field.name, val)}
          formValues={formValues}
          validationState={validationErrors[field.name] ? 'invalid' : 'valid'}
        />
      );
    } else if (field.type === 'multi_select') {
      // You can add your MultiSelectComponent here
      fieldComponent = <span />;
    } else if (field.type === 'datepicker') {
      // You can add your DatePickerComponent here
      fieldComponent = <span />;
    } else if (field.type === 'switch') {
      // You can add your SwitchComponent here
      fieldComponent = <span />;
    } else if (fieldType === 'repeatable') {
      // You can add your RepeatableFieldComponent here
      fieldComponent = <span />;
    } else if (fieldType === 'spacer') {
      fieldComponent = <div style={{ height: 16 }} />;
    }
    return (
      <div
        key={field.id}
        style={field.colSpan ? { gridColumn: `span ${field.colSpan}` } : {}}
      >
        <View UNSAFE_className={styles.formField}>{fieldComponent}</View>
      </div>
    );
  };

  // Render all sections and fields
  const renderSections = () => {
    if (!template) {
      return (
        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
          <Text>Loading form template...</Text>
        </div>
      );
    }
    return template.sections.map((section: any, idx: number) => (
      <div key={idx}>
        {section.heading && (
          <div className={styles.formSectionHeading}>{section.heading}</div>
        )}
        <div
          className={styles.formGrid}
          style={{
            gridTemplateColumns: `repeat(${section.columns || 2}, 1fr)`
          }}
        >
          {section.fields.map(renderField)}
        </div>
      </div>
    ));
  };

  if (loading) return <div>Loading...</div>;

  return (
    <>
      {alert && (
        <EnhancedAlertBanner
          variant={alert.variant}
          message={alert.message}
          onClose={() => setAlert(null)}
          autoHide={alert.variant === 'success'}
          autoHideDelay={5000}
        />
      )}
      <DialogTrigger
        isOpen={isOpen}
        onOpenChange={open => {
          if (!open) onClose();
        }}
      >
        <></>
        {close => (
          <Dialog size="S" UNSAFE_className={styles.editDialog}>
            <Content UNSAFE_className={styles.content}>
              <Flex
                direction="column"
                alignItems="center"
                justifyContent="center"
                UNSAFE_className={styles.formContainer}
              >
                <div className={styles.headerRow}>
                  <Heading level={2} UNSAFE_className={styles.formHeader}>
                    Edit
                  </Heading>
                  <button
                    type="button"
                    aria-label="Close"
                    className={styles.closeButton}
                    onClick={onClose}
                  >
                    <img src={closeIcon} alt="" width={20} height={20} />
                  </button>
                </div>
                <Form UNSAFE_className={styles.rsdForm}>
                  {renderSections()}
                  <View UNSAFE_className={styles.formActions}>
                    <Button
                      variant="secondary"
                      onPress={onClose}
                      UNSAFE_className={styles.formButton}
                      isDisabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="secondary"
                      onPress={handleDelete}
                      UNSAFE_className={styles.formButton}
                      isDisabled={isSubmitting}
                    >
                      Delete
                    </Button>
                    <Button
                      variant="secondary"
                      onPress={handleMarkAsComplete}
                      UNSAFE_className={styles.formButton}
                      isDisabled={isSubmitting}
                    >
                      Mark as Complete
                    </Button>
                    <Button
                      variant="accent"
                      onPress={handleSubmit}
                      UNSAFE_className={`${styles.formButton} ${styles.saveButton}`}
                      isDisabled={isSubmitting}
                    >
                      {isSubmitting ? 'Saving...' : 'Save'}
                    </Button>
                  </View>
                </Form>
              </Flex>
            </Content>
          </Dialog>
        )}
      </DialogTrigger>

      {/* Delete Confirmation Dialog - Using Portal to render at body level */}
      {showDeleteConfirmation &&
        ReactDOM.createPortal(
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 99999
            }}
            onClick={e => {
              // Close if clicking on backdrop
              if (e.target === e.currentTarget) {
                handleCancelDelete();
              }
            }}
          >
            <div
              style={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                padding: '30px',
                maxWidth: '400px',
                width: '90%',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
              }}
            >
              <h2
                style={{
                  color: '#000',
                  fontFamily: "'Adobe Clean', Arial, sans-serif",
                  fontSize: '20px',
                  fontWeight: 500,
                  lineHeight: 1.3,
                  margin: '0 0 16px 0'
                }}
              >
                Delete {itemType}?
              </h2>
              <p
                style={{
                  color: '#6d6d6d',
                  fontFamily: "'Adobe Clean', Arial, sans-serif",
                  fontSize: '14px',
                  lineHeight: 1.5,
                  margin: '0 0 24px 0'
                }}
              >
                Are you sure you want to delete this {itemType.toLowerCase()}?
                This action cannot be undone.
              </p>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '12px'
                }}
              >
                <button
                  onClick={handleCancelDelete}
                  disabled={isSubmitting}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '16px',
                    background: '#e6e6e6',
                    color: '#222222',
                    fontFamily: "'Adobe Clean', Arial, sans-serif",
                    fontSize: '14px',
                    fontWeight: 700,
                    border: 'none',
                    padding: '6px 16px 8px 16px',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    opacity: isSubmitting ? 0.5 : 1
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={isSubmitting}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '16px',
                    background: '#d7373f',
                    color: '#ffffff',
                    fontFamily: "'Adobe Clean', Arial, sans-serif",
                    fontSize: '14px',
                    fontWeight: 700,
                    border: 'none',
                    padding: '6px 16px 8px 16px',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    opacity: isSubmitting ? 0.5 : 1
                  }}
                >
                  {isSubmitting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default EditEventModal;
